/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  isEmpty,
  chain,
  every,
  keyBy,
  mapValues,
  isEqual,
  isArray,
  compact,
  split,
  remove,
} from 'lodash';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Input,
  AppBar,
  Link,
} from '@mui/material';
import styles from './VendorAttribution.module.css';
import AttributionTextInputs from './AttributionTextInputs';
import AttributionSelectInputs from './AttributionSelectInputs';
import AttributeSwitches from './AttributeSwitches';
import AttributeSliders from './AttributeSliders';
import ButtonSwitch from './ButtonSwitch';
import {
  CoreCompetencies,
  TextInputFields,
  SelectInputFields,
  SwitchFields,
} from '../../Lib/Constants/vendor-attribution';
import validateEmail from '../../Lib/Utils/Helpers/validate-email';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import {
  getRequiredFields,
  hasRelatedPartyExplanation,
  findMissingFields,
} from '../../Lib/Utils/FormUtils/vendor-attribution';
import Button from '../ReusableComponents/Button/Button';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import Logo from '../../Assets/images/logo.svg';

const makeValues = (form, fields) =>
  chain(fields)
    .keyBy()
    .mapValues((key) => form[key])
    .value();

/**
 * Convert value being passed in as prop to an array
 *
 * @param value prop being passed in
 */
const splitString = (value) => compact(split(value, ','));

/**
 * Check if string prop is null, if it is return empty string
 *
 * @param {string} value prop beding passed in
 */
const defaultString = (value) => (value && value.trim()) || '';

const defaultVendorAttributes = {
  company_name: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  consumer_segment: '',
  fabric_classification: '',
  product_type: '',
  region: '',
  country: '',
  full_package_vendor: null,
  third_party_compliant: null,
  third_party_garment_testing: null,
  plus_size_manufacturer: null,
  stock_goods: null,
  fn_trim_capability: null,
  cut_to_order: null,
  exclusive_style_manufacturer: null,
  product_development_capabilities: null,
  pattern_maker: null,
  current_customers: '',
  minimum_order_quantity: 0,
  maximum_order_quantity: 0,
  product_lead_time_min: 0,
  product_lead_time_max: 0,
  core_capabilities: '',
  related_party_statement: null,
  related_party_desc: '',
};

export default function VendorAttributionForm(props) {
  const {
    createVendorAttributes = () => {},
    vendorAttributes = defaultVendorAttributes,
    updateVendorAttributes,
    vendorProfile = false,
    error = null,
    location,
    perms,
  } = props;

  const {
    company_name,
    first_name,
    last_name,
    email,
    phone,
    consumer_segment,
    fabric_classification,
    product_type,
    region,
    country,
    full_package_vendor,
    third_party_compliant,
    third_party_garment_testing,
    plus_size_manufacturer,
    stock_goods,
    fn_trim_capability,
    cut_to_order,
    exclusive_style_manufacturer,
    product_development_capabilities,
    pattern_maker,
    current_customers,
    minimum_order_quantity,
    maximum_order_quantity,
    product_lead_time_min,
    product_lead_time_max,
    core_capabilities,
    related_party_statement,
    related_party_desc,
  } = vendorAttributes;

  const [form, setValues] = useState({
    company_name: defaultString(company_name),
    first_name: defaultString(first_name),
    last_name: defaultString(last_name),
    email: defaultString(email),
    phone: defaultString(phone),
    consumer_segment: splitString(consumer_segment),
    fabric_classification: splitString(fabric_classification),
    product_type: splitString(product_type),
    region: splitString(region),
    country: splitString(country),
    full_package_vendor,
    third_party_compliant,
    third_party_garment_testing,
    plus_size_manufacturer,
    stock_goods,
    fn_trim_capability,
    cut_to_order,
    exclusive_style_manufacturer,
    product_development_capabilities,
    pattern_maker,
    current_customers: defaultString(current_customers),
    minimum_order_quantity,
    maximum_order_quantity,
    product_lead_time_min,
    product_lead_time_max,
    core_capabilities: splitString(core_capabilities),
    related_party_statement,
    related_party_desc: defaultString(related_party_desc),
  });

  const [emptyFields, setEmptyFields] = useState({});
  const [invalidEmail, setEmail] = useState(null);
  const [submitted, isSubmitted] = useState(false);

  const onChange = (name, value) => {
    setValues({
      ...form,
      [name]: value,
    });
  };

  const onSwitchChange = (name, value) => {
    setValues({
      ...form,
      [name]: value,
    });

    setEmptyFields({
      ...emptyFields,
      [name]: false,
    });
  };

  const onBlur = (name, value) => {
    setEmptyFields({
      ...emptyFields,
      [name]: isEmpty(value),
    });

    if (isEqual(name, 'email')) {
      const emailValid = validateEmail(form.email);
      setEmail(!emailValid);
    }
  };

  function validateForm(formObject, isVendorProfile) {
    const requiredFields = getRequiredFields(formObject, isVendorProfile);
    const fieldsFilled = every(requiredFields);

    return fieldsFilled;
  }

  function currentUserId() {
    return location.pathname.split('/')[2];
  }

  const onSliderChange = (name, [min, max]) => {
    if (isEqual(name, 'moq')) {
      setValues({
        ...form,
        minimum_order_quantity: min,
        maximum_order_quantity: max,
      });
    }

    if (isEqual(name, 'production_lead_time')) {
      setValues({
        ...form,
        product_lead_time_min: min,
        product_lead_time_max: max,
      });
    }
  };

  const onDelete = (stateField, value) => {
    const stateArray = form[stateField];
    remove(stateArray, (v) => isEqual(v, value));
    setValues({ ...form, [stateField]: stateArray });
  };

  function submitForm(isVendorProfile) {
    const isValid = validateForm(form, isVendorProfile);
    const submittableForm = mapValues(form, (value) => {
      if (isArray(value)) return value.join(',');
      return value;
    });

    if (!isValid) {
      const missingFields = mapValues(
        keyBy(findMissingFields(submittableForm)),
        () => true,
      );

      setEmptyFields(missingFields);
    }

    if (every([isValid, !invalidEmail, !vendorProfile])) {
      createVendorAttributes(submittableForm);
      isSubmitted(true);
    }
    if (every([isValid, !invalidEmail, vendorProfile])) {
      updateVendorAttributes(currentUserId(), submittableForm);
      isSubmitted(true);
    }
  }

  return (
    <div className={styles.attributionContainer}>
      {!vendorProfile && (
        <AppBar position="static" className={styles.appBar}>
          <div className={styles.appBarContent}>
            <h3 className={styles.attributionHeader}>Vendor Form</h3>
            <Link to="/" className={styles.link}>
              <img src={Logo} alt="" className={styles.logo} />
            </Link>
          </div>
        </AppBar>
      )}
      <form className={styles.container}>
        {!vendorProfile && (
          <AttributionTextInputs
            values={makeValues(form, TextInputFields)}
            onChange={onChange}
            onBlur={onBlur}
            invalidEmail={invalidEmail}
            emptyFields={emptyFields}
            required={!vendorProfile}
            disabled={!perms.canUpdate}
          />
        )}
        <AttributionSelectInputs
          values={makeValues(form, SelectInputFields)}
          onChange={onChange}
          onBlur={onBlur}
          emptyFields={emptyFields}
          onDelete={onDelete}
          disabled={!perms.canUpdate}
        />
        <AttributeSwitches
          values={makeValues(form, SwitchFields)}
          onChange={onSwitchChange}
          onBlur={onBlur}
          emptyFields={emptyFields}
          disabled={!perms.canUpdate}
        />
        <div>
          <TextInput
            required
            className={styles.outlinedInput}
            error={!!emptyFields.current_customers}
            label="Current Customers"
            id="vatt-current-customers"
            multiline
            name="current_customers"
            maxRows={5}
            margin="dense"
            onBlur={({ target: { name, value } }) => onBlur(name, value)}
            value={form.current_customers}
            onChange={({ target: { name, value } }) => onChange(name, value)}
            disabled={!perms.canUpdate}
          />
        </div>
        <AttributeSliders
          onChange={onSliderChange}
          minimum_order_quantity={form.minimum_order_quantity}
          maximum_order_quantity={form.maximum_order_quantity}
          production_lead_time_min={form.product_lead_time_min}
          production_lead_time_max={form.product_lead_time_max}
          emptyFields={emptyFields}
          disabled={!perms.canUpdate}
        />
        <div>
          <SelectInput
            multiSelect
            required
            className={styles.multiSelect}
            error={!!emptyFields.core_capabilities}
            label="Capabilities/Core Competencies"
            multiple
            name="core_capabilities"
            onChange={({ target: { name, value } }) => onChange(name, value)}
            onBlur={({ target: { name, value } }) => onBlur(name, value)}
            value={form.core_capabilities}
            id="vatt-core-competencies"
            input={<Input name="core-competencies" />}
            options={CoreCompetencies.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
            onDelete={onDelete}
            disabled={!perms.canUpdate}
          />
        </div>
        <div>
          <div className={styles.switchContainer}>
            <FormControl
              variant="standard"
              required
              className={styles.buttonSwitchControl}
              error={!!emptyFields.related_party_statement}
            >
              <InputLabel
                htmlFor="vatt-rps"
                className={styles.relatedPartyLabel}
              >
                {/* eslint-disable prettier/prettier */}
                <strong className={styles.strongLabel}>
                  RELATED PARTY STATEMENT:
                </strong>{' '}
                Are you aware of any direct or indirect material relationship
                between this vendor (or any of its employees or representatives)
                and any Fashion Nova employee (including their immediate family
                members)?
                {/* eslint-enable prettier/prettier */}
              </InputLabel>
              <ButtonSwitch
                id="vatt-rps"
                onChange={(selected) =>
                  onSwitchChange('related_party_statement', selected)
                }
                selected={form.related_party_statement}
                disabled={!perms.canUpdate}
              />
            </FormControl>
          </div>
          {form.related_party_statement && (
            <TextInput
              required
              className={styles.outlinedInput}
              error={!hasRelatedPartyExplanation(form)}
              label="if YES, explain"
              id="vatt-related-party-desc"
              multiline
              name="related_party_desc"
              maxRows={5}
              onBlur={({ target: { name, value } }) => onBlur(name, value)}
              value={form.related_party_desc}
              onChange={({ target: { name, value } }) => onChange(name, value)}
              margin="dense"
              disabled={!perms.canUpdate}
            />
          )}
        </div>
        {perms.canUpdate && (
          <Button
            className={styles.submitButton}
            color="white"
            onClick={() => submitForm(vendorProfile)}
            disabled={submitted && !vendorProfile}
          >
            {submitted && !vendorProfile ? 'Submitted' : 'Submit'}
          </Button>
        )}
        {error && <p className={styles.error}>{error.message}</p>}
        {submitted && !error && (
          <p className={styles.success}>
            Vendor Attributes have been submitted.
          </p>
        )}
      </form>
    </div>
  );
}

VendorAttributionForm.propTypes = {
  createVendorAttributes: PropTypes.func,
  updateVendorAttributes: PropTypes.func.isRequired,
  vendorAttributes: PropTypes.shape({}),
  vendorProfile: PropTypes.bool,
  error: PropTypes.shape({}),
  location: locationProps.isRequired,
  perms: PropTypes.shape({
    canRead: PropTypes.bool,
    canUpdate: PropTypes.bool,
    canDelete: PropTypes.bool,
    canCreate: PropTypes.bool,
  }).isRequired,
};
